import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { mumsApi } from "./mumsApi";
import { Loader2, Camera, BookOpen } from "lucide-react";

export default function MumsSummary() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let off = false;
    mumsApi.dashboard().then((d) => { if (!off) setData(d); }).catch(() => {});
    return () => { off = true; };
  }, []);
  if (!data) return (
    <div className="text-center py-20 text-[var(--mum-cobalt)]" data-testid="summary-loading">
      <Loader2 size={28} className="animate-spin mx-auto mb-2" />Gathering today&apos;s garden…
    </div>
  );
  const t = data.totals;
  return (
    <div className="space-y-8" data-testid="mums-summary">
      <header className="text-center">
        <div className="mum-chip mx-auto">Garden Summary</div>
        <h1 className="font-display text-4xl mt-2 text-[var(--mum-sapphire)] leading-none">Today&apos;s Garden Snapshot</h1>
        <p className="font-display italic text-[var(--mum-cobalt)] mt-1">
          {new Date().toLocaleDateString(undefined, { dateStyle: "long" })}
        </p>
      </header>

      <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="summary-stats">
        <Stat label="Dahlia Varieties" num={t.dahlia_varieties} />
        <Stat label="Tubers in Storage" num={t.tubers_in_storage} />
        <Stat label="Tubers in Ground" num={t.tubers_in_ground} />
        <Stat label="Blooming" num={t.blooming_plants} accent />
        <Stat label="In Bud" num={t.budding_plants} />
        <Stat label="Growing on" num={t.growing_plants} />
        <Stat label="Flower Varieties" num={t.flower_varieties} />
        <Stat label="Seed Varieties" num={t.seed_varieties} />
        <Stat label="Seeds to Sow" num={t.seeds_to_sow} />
        <Stat label="Photos" num={t.photos} />
        <Stat label="Journal Entries" num={t.journal_entries} />
        <Stat label="Collection Value" num={`$${t.collection_value_nzd.toLocaleString()}`} />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="mums-card p-5" data-testid="summary-flowering">
          <h2 className="font-display text-2xl mb-1">Currently Blooming</h2>
          <p className="text-xs italic text-[var(--mum-cobalt)]/75">in your garden right now</p>
          {data.flowering_now.length === 0 ? (
            <p className="font-display italic text-[var(--mum-cobalt)]/70 mt-4">Nothing in flower yet — patience is the gardener&apos;s gift.</p>
          ) : (
            <ul className="mt-4 space-y-2">
              {data.flowering_now.map((b) => (
                <li key={b.id} className="flex items-center justify-between border-b border-[var(--mum-sky)] py-2 last:border-0">
                  <span className="font-display text-lg text-[var(--mum-sapphire)]">{b.name}</span>
                  {b.bed && <span className="text-xs text-[var(--mum-cobalt)]/70">Bed {b.bed}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mums-card p-5" data-testid="summary-latest-journal">
          <h2 className="font-display text-2xl mb-1">Latest Journal Entry</h2>
          {data.latest_journal ? (
            <div className="mt-3">
              <div className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">
                {prettyDate(data.latest_journal.date)} {data.latest_journal.auto && "· auto"}
              </div>
              {data.latest_journal.title && (
                <h3 className="font-display text-xl mt-1 text-[var(--mum-sapphire)]">{data.latest_journal.title}</h3>
              )}
              <p className="whitespace-pre-wrap mt-2 text-sm leading-relaxed">{data.latest_journal.body}</p>
              <Link to="/mums-shed/journal" className="mum-btn-ghost mt-4 inline-flex"><BookOpen size={13} /> Open journal</Link>
            </div>
          ) : (
            <p className="font-display italic text-[var(--mum-cobalt)]/70 mt-4">No entries yet — your story begins today.</p>
          )}
        </div>
      </section>

      <section className="mums-card p-5" data-testid="summary-photos">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl">Latest Garden Photos</h2>
          <Link to="/mums-shed/gallery" className="mum-btn-ghost"><Camera size={13} /> Full gallery</Link>
        </div>
        {data.latest_photos.length === 0 ? (
          <p className="font-display italic text-[var(--mum-cobalt)]/70 mt-4">Photographs will appear here as you add them.</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-4">
            {data.latest_photos.map((p, i) => (
              <div key={i} className="aspect-square overflow-hidden rounded-lg border border-[var(--mum-cornflower)]">
                <img src={p.url} alt={p.ref_name} className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, num, accent }) {
  return (
    <div className={`mum-stat ${accent ? "ring-1 ring-[var(--mum-cobalt)]" : ""}`}>
      <div className="num" data-testid={`mum-stat-num-${label.replace(/\s+/g, "-").toLowerCase()}`}>{num}</div>
      <div className="label">{label}</div>
    </div>
  );
}

function prettyDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }); }
  catch { return iso; }
}
